import type { AccountSnapshot, Activity, FieldOutbox, LocalState, PostMetric, TopicStatus } from "../types";
import { snapshotDuplicateKey, postDuplicateKey } from "./importing";
import { mergeById } from "./merge";
import { statusChangeMessage } from "./sync";

export function reduceSetStatus(
  state: LocalState,
  topicId: string,
  status: TopicStatus,
  now: string,
  ids: { outboxId: string; activityId: string },
): LocalState {
  const topic = state.topics.find((item) => item.id === topicId);
  if (!topic || topic.inTrash || topic.status === status) return state;

  const pending = state.outbox.find(
    (item): item is Extract<typeof item, { kind: "set_status" }> =>
      item.kind === "set_status" && item.topicId === topicId,
  );
  const fromStatus = pending?.fromStatus ?? topic.status;
  const activityId = pending?.activityId ?? ids.activityId;
  const activity: Activity = {
    id: activityId,
    topicId,
    type: "status_change",
    fromStatus,
    toStatus: status,
    message: statusChangeMessage(fromStatus, status),
    createdAt: pending ? state.activities.find((item) => item.id === activityId)?.createdAt ?? now : now,
    updatedAt: now,
    deletedAt: null,
  };

  const outboxItem = {
    id: pending?.id ?? ids.outboxId,
    kind: "set_status" as const,
    topicId,
    status,
    baseLastEditedTime: pending?.baseLastEditedTime ?? topic.lastEditedTime,
    activityId,
    fromStatus,
    updatedAt: now,
    lastError: null,
  };

  return {
    ...state,
    topics: state.topics.map((item) =>
      item.id === topicId ? { ...item, status, syncError: null, updatedAt: now } : item,
    ),
    activities: mergeById(
      state.activities.filter((item) => item.id !== activityId),
      [activity],
    ),
    outbox: [...state.outbox.filter((item) => item.id !== outboxItem.id), outboxItem],
  };
}

export function reducePatchFields(
  state: LocalState,
  patch: Omit<FieldOutbox, "id" | "kind" | "lastError" | "updatedAt" | "baseLastEditedTime"> & {
    topicId: string;
  },
  now: string,
  outboxId: string,
): LocalState {
  const topic = state.topics.find((item) => item.id === patch.topicId);
  if (!topic) return state;
  const pending = state.outbox.find(
    (item): item is FieldOutbox => item.kind === "patch_fields" && item.topicId === patch.topicId,
  );
  const item: FieldOutbox = {
    id: pending?.id ?? outboxId,
    kind: "patch_fields",
    topicId: patch.topicId,
    baseLastEditedTime: pending?.baseLastEditedTime ?? topic.lastEditedTime,
    updatedAt: now,
    lastError: null,
    publishUrl: patch.publishUrl === undefined ? pending?.publishUrl : patch.publishUrl,
    plannedPublishDate:
      patch.plannedPublishDate === undefined ? pending?.plannedPublishDate : patch.plannedPublishDate,
    platforms: patch.platforms === undefined ? pending?.platforms : patch.platforms,
    notes: patch.notes === undefined ? pending?.notes : patch.notes,
    queueOrder: patch.queueOrder === undefined ? pending?.queueOrder : patch.queueOrder,
  };
  return {
    ...state,
    topics: state.topics.map((entry) =>
      entry.id === patch.topicId
        ? {
            ...entry,
            publishUrl: item.publishUrl === undefined ? entry.publishUrl : item.publishUrl,
            plannedPublishDate:
              item.plannedPublishDate === undefined ? entry.plannedPublishDate : item.plannedPublishDate,
            platforms: item.platforms === undefined ? entry.platforms : item.platforms,
            notes: item.notes === undefined ? entry.notes : item.notes,
            notesUpdatedAt: item.notes === undefined ? entry.notesUpdatedAt : now,
            queueOrder: item.queueOrder === undefined ? entry.queueOrder : item.queueOrder,
            syncError: null,
            updatedAt: now,
          }
        : entry,
    ),
    outbox: [...state.outbox.filter((entry) => entry.id !== item.id), item],
  };
}

export function reduceQueueOrder(
  state: LocalState,
  orderedIds: readonly string[],
  now: string,
  outboxId: () => string,
): LocalState {
  let next = state;
  orderedIds.forEach((topicId, index) => {
    const topic = next.topics.find((item) => item.id === topicId);
    const order = index + 1;
    if (!topic || topic.queueOrder === order) return;
    next = reducePatchFields(next, { topicId, queueOrder: order }, now, outboxId());
  });
  return next;
}

export function reduceUpsertSnapshot(state: LocalState, snapshot: AccountSnapshot, overwrite: boolean): LocalState {
  const key = snapshotDuplicateKey(snapshot);
  const existing = state.snapshots.find(
    (item) => item.deletedAt == null && snapshotDuplicateKey(item) === key && item.id !== snapshot.id,
  );
  if (existing && !overwrite) return state;
  const next = existing
    ? { ...snapshot, id: existing.id, updatedAt: snapshot.updatedAt }
    : snapshot;
  return {
    ...state,
    snapshots: mergeById(
      state.snapshots.filter((item) => item.id !== existing?.id),
      [next],
    ),
  };
}

export function reduceDeleteSnapshot(state: LocalState, snapshotId: string, now: string): LocalState {
  return {
    ...state,
    snapshots: state.snapshots.map((item) =>
      item.id === snapshotId ? { ...item, deletedAt: now, updatedAt: now } : item,
    ),
  };
}

export function reduceUpsertPost(state: LocalState, post: PostMetric, overwrite: boolean): LocalState {
  const key = postDuplicateKey(post);
  const existing = state.postMetrics.find(
    (item) => item.deletedAt == null && postDuplicateKey(item) === key && item.id !== post.id,
  );
  if (existing && !overwrite) return state;
  const next = existing ? { ...post, id: existing.id } : post;
  return {
    ...state,
    postMetrics: mergeById(
      state.postMetrics.filter((item) => item.id !== existing?.id),
      [next],
    ),
  };
}

export function reduceDeletePost(state: LocalState, postId: string, now: string): LocalState {
  return {
    ...state,
    postMetrics: state.postMetrics.map((item) =>
      item.id === postId ? { ...item, deletedAt: now, updatedAt: now } : item,
    ),
  };
}
